/**
 * O botão de baixar o programa do notebook.
 *
 * Ele aparece em **dois lugares**, e os dois de propósito:
 *
 * - **dentro do painel, para qualquer conta que entrou**, sem olhar permissão nenhuma. Baixar o
 *   instalador não é decisão de dinheiro nem de sistema: é pegar o programa;
 * - **na tela de entrar**, antes de qualquer senha. É o caso que resolve o problema de verdade:
 *   computador novo, ou notebook formatado, e quem está na frente dele ainda não tem o programa
 *   para abrir. Se o botão só existisse depois da senha, ele não serviria justamente na hora em
 *   que é preciso.
 *
 * **Três coisas que ele não faz, e cada uma tem motivo.**
 *
 * Ele **não aparece** quando quem hospeda não configurou o endereço: nenhum endereço escrito no
 * código para servir de reserva, e nenhum aviso de erro. Quem recebe este sistema de doação pode
 * não ter repositório de instalador nenhum, e um botão que leva ao instalador de outra igreja é
 * pior do que botão nenhum.
 *
 * Ele **não desaparece** quando a versão não vem. O número é lido lá fora, de um lugar que pode
 * estar fora do ar; o endereço do download, não. Então sem o número ele aparece igual, só sem
 * dizer qual é a versão, porque baixar continua funcionando.
 *
 * E ele **não grita**. A moldura é azul, de "está em ordem", e não dourada, que neste painel é a
 * cor de pendência. Quem abre o painel abre para ver um número do mês, não para baixar programa.
 */
import { useEffect, useState, type JSX } from 'react'
import { servidor, type Instalador } from '../api'

/** `08/10/2026`, do jeito que a data vem do servidor. */
function dia(data: string): string {
  const partes = data.slice(0, 10).split('-')
  return partes.length === 3 ? `${partes[2]}/${partes[1]}/${partes[0]}` : data
}

export function BaixarOPrograma(): JSX.Element | null {
  const [instalador, setInstalador] = useState<Instalador | null>(null)

  /*
     Perguntado ao abrir a tela, e nada é guardado em lugar nenhum.

     Versão guardada envelhece: um número gravado continuaria dizendo a versão velha até alguém
     lembrar de atualizar. Lido na hora, ele não tem como estar velho. Quem guarda por alguns
     minutos é o servidor, para não bater no GitHub a cada abertura de tela.
   */
  useEffect(() => {
    let vale = true

    servidor
      .instalador()
      .then((recebido) => {
        if (vale) setInstalador(recebido)
      })
      /*
         Falhou: nenhum aviso na tela, e nenhum botão.

         Esta é a única tela do painel que engole o próprio erro, e é escolha. Quem está na tela
         de entrar veio entrar; um recado vermelho de "não deu para saber do instalador" em cima
         do campo de senha assustaria sem ajudar ninguém.
       */
      .catch(() => undefined)

    return () => {
      vale = false
    }
  }, [])

  if (!instalador || !instalador.tem_instalador) return null

  return (
    <section className="baixar-o-programa so-tela">
      <a
        className="como-botao secundario"
        href={instalador.endereco}
        /*
           Abre numa aba nova, e a do painel fica onde estava: quem está na tela de entrar não
           perde o e-mail já digitado, e quem está dentro não perde a sessão de vista. O
           `noreferrer` é o que impede a página do download de saber de onde a pessoa veio.
         */
        target="_blank"
        rel="noopener noreferrer"
      >
        Baixar o programa do notebook
      </a>
      <p className="explicacao miudo">
        {instalador.versao === null ? (
          /*
             Sem o número: a frase diz que o download é da versão mais nova, que é a verdade (o
             endereço aponta para a última), e não finge que sabe qual é.
           */
          <>O endereço abre a versão mais nova. Para instalar no computador da tesouraria.</>
        ) : (
          <>
            Versão {instalador.versao}
            {instalador.publicada_em !== null && <>, de {dia(instalador.publicada_em)}</>}. Para
            instalar no computador da tesouraria.
          </>
        )}
      </p>
    </section>
  )
}
